import Connector from '@nextories/vue-core-components/src/utils/Api';
import type { Data } from '@nextories/vue-core-components/src/types/global';

export default class Api extends Connector {
  /**
   * Retrieve the project of the current user from the backend
   * @returns {Promise<Object>} resolve with the project for the current user
   * or reject with the backend error message
   */

  // START AUTH
  login(request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', 'account/login/', request);
  }

  autoLogin(request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', 'account/auto-login/', request);
  }

  getImpersonationProjects() {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', 'account/impersonation/projects/');
  }

  recoverPassword(request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', 'account/password/reset/request/', request);
  }

  resetPassword(request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('patch', 'account/password/reset/', request);
  }

  checkPasswordKey(request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', 'account/password/reset/check-key/', request);
  }
  // END AUTH

  // START CONTACTS
  getContacts(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/support/${id}`);
  }

  postContact(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/support/${id}`, request, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
  // END CONTACTS

  // COMPARATOR & SIGNATURE ENDPOINTS
  getSignature(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/signature/${id}`);
  }

  activateSignature(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/signature/activate/${id}`, request);
  }

  getStripeToken() {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', 'customer/setup-stripe');
  }
  // END COMPARATOR

  // START DECLARATION
  getDeclaration(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/value/${id}`);
  }

  postDeclaration(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/value/${id}`, request);
  }

  patchDeclaration(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('patch', `customer/value/${id}`, request);
  }
  // END DECLARATION

  // START DEMAND
  getDemand(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/client-data/${id}`);
  }

  getAddresses(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/addresses/${id}`);
  }

  patchAddress(id: string, addressId: number, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('patch', `customer/addresses/${id}/${addressId}`, request);
  }

  getContractsTransfer(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/contracts-transfer/${id}`);
  }

  postValorDeclaration(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/upload-dv/${id}`, request, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  postCardToken(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/register-stripe/${id}`, request);
  }

  postContractsTransfer(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/contracts-transfer/${id}`, request);
  }
  // END DEMAND

  // START INVENTORY
  getRooms(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/inventory/${id}`);
  }

  getServices(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/special-services/${id}`);
  }

  postInventory(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/inventory/${id}`, request);
  }
  // END INVENTORY

  // START OFFER
  getOffers(id: string, isDisplayMoverPhotos?: Boolean) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/offers/${id}${isDisplayMoverPhotos ? '?display_mover_photos=true' : ''}`);
  }

  preBookOffer(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/prebook/${id}`, request);
  }

  confirmPaymentOffer(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/confirm-payment/${id}`, request);
  }

  bookOffer(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/book/${id}`, request);
  }

  postOptions(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/select-options/${id}`, request);
  }

  getOffer(token: string, id: string, isDisplayMoverPhotos?: boolean) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/devis-resume/${token}?`
      + `devis_id=${id}${isDisplayMoverPhotos ? '&display_mover_photos=true' : ''}`);
  }
  // END OFFER

  // START PROFILE
  getCardsPayment(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/card-info-stripe/${id}`);
  }

  updateCardPayment(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/card-info-stripe/${id}`, request);
  }

  deleteCardPayment(project: string, id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('delete', `customer/card-info-stripe/${project}?pm_id=${id}`);
  }

  getProfile() {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', 'account/profile/');
  }

  updateEmail(request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('patch', 'account/email/update/', request);
  }

  postPassword(request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', 'account/password/', request);
  }
  // END PROFILE

  // START PROJECT
  getProjects() {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', 'customer/projects/');
  }

  getMeetingsSlots(id: string, request: {
    params: {}
  }) {
    const { params } = request;
    const query = new URLSearchParams(params);
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', this.formattedUrl(`meeting/slot/${id}?${query.toString()}`));
  }

  postMeeting(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `meeting/slot/${id}`, request);
  }

  deleteMeeting(id: string, request: {}) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `meeting/slot/${id}/delete/`, request);
  }
  // END PROJECT

  // START PROJECT
  getAddServices(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/additional-services/${id}`);
  }

  postServices(id: string, request: Data) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/additional-services/${id}`, request);
  }
  // END PROJECT

  // START INVOICE
  getInvoices(id: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/invoices/${id}`);
  }

  getInvoiceDownload(projectId: string, invoiceId: string) {
    // Use axios request API so `responseType` is guaranteed to be applied.
    return this.api.request({
      method: 'get',
      url: `customer/invoices/${projectId}/${invoiceId}/download`,
      responseType: 'blob',
    });
  }
  // END INVOICE
  
  // START PAYMENT
  getPaymentTypes(token: string, quoteId: string, request: { payment_types: string[] }) {
    const query = new URLSearchParams(request);
    return this._methodData('get', `customer/payment-types/${token}/${quoteId}?${query.toString()}`);
  }
  
  createBankTransfer(token: string, quoteId: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/create-payment/bank-transfer/${token}/${quoteId}`);
  }
  // END PAYMENT
  
  // START INSTALLMENT PAYMENT
  getSimulateInstallmentPayment(token: string, quoteId?: string, amount?: number) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('get', `customer/floa-payment/${token}/simulated-plans?${quoteId ? 'quote_id=' + quoteId : ''}${amount ? 'amount=' + amount : ''}`);
  }
  
  postInstallmentPayment(token: string, request: {
    quote_id: string;
    device: string;
  }) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/floa-payment/${token}/deals`, request);
  }
  
  postInstallmentPaymentPrebookCheck(token: string, quoteId: string) {
    // eslint-disable-next-line no-underscore-dangle
    return this._methodData('post', `customer/floa-payment/prebook-check/${token}`, {
      quote_id: quoteId,
    });
  }
  // END INSTALLMENT PAYMENT
} 
